import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, Check, PackageCheck, Truck } from "lucide-react";

import {
  usePostReceipt,
  usePurchaseOrder,
  useReceivableOrders,
} from "@/api/queries";
import type { OrderLine } from "@/api/types";
import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { Field, Input, Select } from "@/components/Input";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { reportMutationError } from "@/lib/mutation-errors";
import { PlacePicker } from "@/features/ingestion/PlacePicker";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import { formatDate, formatQuantity, formatUnit } from "@/lib/format";

interface Counted {
  declared: string;
  accepted: string;
  rejected: string;
  reason: string;
}

/**
 * The receiving screen (Frontend Spec §20, PHASE 10).
 *
 * Built for someone standing at a truck with a phone: one order, one line at a
 * time, three numbers each.
 *
 * * **déclaré** — what the slip claims;
 * * **accepté** — what was counted and is going on the shelf. The only one that
 *   becomes stock;
 * * **quarantaine** — damaged or wrong. Recorded, never received, and it stays
 *   owed on the order.
 *
 * The écart is shown the moment the three disagree, not after confirming: a
 * warning that arrives once the movement is posted is a warning about something
 * you can no longer choose differently.
 */
export function ReceivingPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  const orderId = params.get("order") ?? "";
  const { data: receivable, isLoading: loadingOrders, error } = useReceivableOrders();
  const { data: detail, isLoading: loadingOrder } = usePurchaseOrder(orderId, {
    enabled: Boolean(orderId),
  });
  const post = usePostReceipt();

  const [counts, setCounts] = useState<Record<string, Counted>>({});
  const [place, setPlace] = useState<{
    location_id: string | null;
    container_id: string | null;
  }>({ location_id: null, container_id: null });
  const [slip, setSlip] = useState("");

  // A fresh order means fresh counts: carrying numbers from the previous
  // delivery over to this one is how the wrong quantity gets received.
  useEffect(() => {
    setCounts({});
    setSlip("");
  }, [orderId]);

  const lines = useMemo(
    () => (detail?.lines ?? []).filter((line) => Number(line.backorder) > 0),
    [detail],
  );

  function set(lineId: string, patch: Partial<Counted>) {
    setCounts((current) => ({
      ...current,
      [lineId]: {
        declared: "",
        accepted: "",
        rejected: "",
        reason: "",
        ...current[lineId],
        ...patch,
      },
    }));
  }

  const entries = Object.entries(counts).filter(
    ([, value]) => Number(value.accepted || 0) > 0 || Number(value.rejected || 0) > 0,
  );
  const needsPlace = entries.some(([, value]) => Number(value.accepted || 0) > 0);
  const ready = entries.length > 0 && (!needsPlace || place.location_id || place.container_id);

  async function confirm() {
    if (!ready) return;
    try {
      const receipt = await post.mutateAsync({
        orderId,
        supplier_reference: slip,
        lines: entries.map(([lineId, value]) => ({
          line_id: lineId,
          declared: value.declared || value.accepted || "0",
          accepted: value.accepted || "0",
          rejected: value.rejected || "0",
          location_id: Number(value.accepted || 0) > 0 ? place.location_id : null,
          container_id: Number(value.accepted || 0) > 0 ? place.container_id : null,
          mismatch_reason: value.reason || null,
        })),
        // A phone on a loading dock: a double tap must not receive twice.
        idempotency_key: `receipt-${orderId}-${entries
          .map(([id, v]) => `${id}:${v.accepted}:${v.rejected}`)
          .join("|")}`,
      });
      toast.success(
        "Réception enregistrée",
        receipt.mismatch_count > 0
          ? `${receipt.code} — ${receipt.mismatch_count} écart(s) noté(s)`
          : receipt.code,
      );
      setCounts({});
      navigate(`/purchase-orders/${orderId}`);
    } catch (caught) {
      reportMutationError(toast, "Réception refusée", caught);
    }
  }

  return (
    <Page>
      <PageHeader
        title="Réception"
        subtitle="Une commande, une ligne à la fois. Seul l'accepté entre en stock."
      />

      <div className="flex min-w-0 flex-col gap-3">
        <Card>
          <CardHeader title="Commande" />
          <CardBody>
            {loadingOrders && <LoadingState label="Chargement des commandes…" />}
            {error && <ErrorState error={error} />}
            {receivable && receivable.length === 0 && (
              <EmptyState
                icon={<PackageCheck className="size-6" aria-hidden />}
                title="Rien à recevoir"
                description="Toutes les commandes envoyées ont été livrées."
              />
            )}
            {receivable && receivable.length > 0 && (
              <Field label="Livraison reçue pour">
                <Select
                  value={orderId}
                  onChange={(event) => {
                    const merged = new URLSearchParams(params);
                    if (event.target.value) merged.set("order", event.target.value);
                    else merged.delete("order");
                    setParams(merged, { replace: true });
                  }}
                  aria-label="Commande à recevoir"
                >
                  <option value="">Choisir une commande…</option>
                  {receivable.map((order) => (
                    <option key={order.id} value={order.id}>
                      {order.code} — {order.supplier_name}
                      {order.reference ? ` (${order.reference})` : ""}
                      {order.expected_on ? ` · attendu ${formatDate(order.expected_on)}` : ""}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
          </CardBody>
        </Card>

        {orderId && loadingOrder && <LoadingState label="Chargement de la commande…" />}

        {orderId && detail && (
          <>
            <Card>
              <CardHeader title={`${detail.order.code} — ${detail.order.supplier_name}`} />
              <CardBody className="flex flex-col gap-3">
                <Field label="Numéro de bordereau" hint="Facultatif. Le leur, sur le papier.">
                  <Input
                    value={slip}
                    onChange={(event) => setSlip(event.target.value)}
                    placeholder="BL-778"
                    aria-label="Numéro de bordereau"
                  />
                </Field>
                <Field
                  label="Emplacement de rangement"
                  hint="Où va ce qui est accepté. La quarantaine n'entre pas en stock."
                >
                  <PlacePicker
                    locationId={place.location_id}
                    containerId={place.container_id}
                    onChange={setPlace}
                  />
                </Field>
              </CardBody>
            </Card>

            {lines.length === 0 ? (
              <EmptyState
                icon={<PackageCheck className="size-6" aria-hidden />}
                title="Cette commande est complète"
                description="Toutes ses lignes ont été reçues."
              />
            ) : (
              <div className="flex flex-col gap-2">
                {lines.map((line) => (
                  <LineCounter
                    key={line.line_id}
                    line={line}
                    counted={counts[line.line_id]}
                    onChange={(patch) => set(line.line_id, patch)}
                  />
                ))}
              </div>
            )}

            <div className="sticky bottom-0 flex items-center justify-between gap-3 rounded-lg border border-border bg-surface-raised p-3">
              <span className="text-[13px] text-ink-muted">
                {entries.length === 0
                  ? "Saisissez au moins une quantité."
                  : `${entries.length} ligne(s) à confirmer`}
              </span>
              <Button
                variant="primary"
                onClick={() => void confirm()}
                disabled={!ready || post.isPending}
                title={
                  needsPlace && !place.location_id && !place.container_id
                    ? "Choisissez où ranger ce qui est accepté."
                    : undefined
                }
              >
                <Check className="size-4" aria-hidden />
                {post.isPending ? "Enregistrement…" : "Confirmer la réception"}
              </Button>
            </div>
          </>
        )}

        {!orderId && receivable && receivable.length > 0 && (
          <EmptyState
            icon={<Truck className="size-6" aria-hidden />}
            title="Choisissez une commande"
            description="Sélectionnez la commande que la livraison concerne pour compter ses lignes."
          />
        )}
      </div>
    </Page>
  );
}

function LineCounter({
  line,
  counted,
  onChange,
}: {
  line: OrderLine;
  counted: Counted | undefined;
  onChange: (patch: Partial<Counted>) => void;
}) {
  const declared = Number(counted?.declared || 0);
  const accepted = Number(counted?.accepted || 0);
  const rejected = Number(counted?.rejected || 0);
  const touched = Boolean(counted?.declared || counted?.accepted || counted?.rejected);
  const mismatch = touched && declared > 0 && declared !== accepted + rejected;
  const over = accepted + rejected > Number(line.backorder);

  return (
    <Card>
      <CardBody className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-[14px] font-medium text-ink">{line.product_name}</p>
            <p className="truncate text-[11px] text-ink-subtle">
              {[line.product_manufacturer, line.product_part_number].filter(Boolean).join(" · ")}
            </p>
          </div>
          <p className="tnum whitespace-nowrap text-[12px] text-ink-muted">
            Commandé {formatQuantity(line.ordered)} · déjà reçu {formatQuantity(line.received)} ·{" "}
            <span className="text-status-reserved">
              reste {formatQuantity(line.backorder)} {formatUnit(line.unit)}
            </span>
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Field label="Déclaré">
            <Input
              type="number"
              min="0"
              step="any"
              value={counted?.declared ?? ""}
              onChange={(event) => onChange({ declared: event.target.value })}
              aria-label={`Quantité déclarée pour ${line.product_name}`}
            />
          </Field>
          <Field label="Accepté">
            <Input
              type="number"
              min="0"
              step="any"
              value={counted?.accepted ?? ""}
              onChange={(event) => onChange({ accepted: event.target.value })}
              aria-label={`Quantité acceptée pour ${line.product_name}`}
            />
          </Field>
          <Field label="Quarantaine">
            <Input
              type="number"
              min="0"
              step="any"
              value={counted?.rejected ?? ""}
              onChange={(event) => onChange({ rejected: event.target.value })}
              aria-label={`Quantité en quarantaine pour ${line.product_name}`}
            />
          </Field>
        </div>

        {mismatch && (
          <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning-subtle px-3 py-2 text-[13px]">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-ink">
                Écart : le bordereau dit {formatQuantity(String(declared))}, vous comptez{" "}
                {formatQuantity(String(accepted + rejected))}.
              </p>
              <Input
                value={counted?.reason ?? ""}
                onChange={(event) => onChange({ reason: event.target.value })}
                placeholder="Ce qui s'est passé — boîtes écrasées, manquantes…"
                className="mt-2"
                aria-label={`Motif de l'écart pour ${line.product_name}`}
              />
            </div>
          </div>
        )}

        {over && (
          <p className="flex items-center gap-2 text-[12px] text-warning">
            <AlertTriangle className="size-4 shrink-0" aria-hidden />
            Plus que ce qui restait dû. C'est accepté et enregistré tel quel.
          </p>
        )}

        {rejected > 0 && (
          <StatusBadge tone="out" className="self-start">
            {formatQuantity(String(rejected))} en quarantaine — n'entre pas en stock
          </StatusBadge>
        )}
      </CardBody>
    </Card>
  );
}
